import { useEffect, useMemo, useState } from 'react';

import { useLoaderContext } from '../../../components/Loader/LoaderContext';

import DashboardHeader from '../components/DashboardHeader';

import UsageFilters from '../components/EmployeeApplicationUsage/UsageFilters';
import UsageLegend from '../components/EmployeeApplicationUsage/UsageLegend';
import UsageSummaryCards from '../components/EmployeeApplicationUsage/UsageSummaryCards';
import UsageTable from '../components/EmployeeApplicationUsage/UsageTable';

import type {
  SummaryCard,
  EmployeeUsage,
} from '../types/EmployeeApplicationUsagePage/employeeApplicationUsage.types';

const summaryCards: SummaryCard[] = [
  {
    title: 'Team Members',
    value: '48',
    subtitle: 'In this report',
  },
  {
    title: 'Avg. Productive',
    value: '07:13:30',
    subtitle: 'Per employee',
  },
  {
    title: 'Avg. Working',
    value: '08:05:24',
    subtitle: 'Per employee',
  },
  {
    title: 'Avg. Productive %',
    value: '89.3%',
    subtitle: 'Productive ÷ Working',
  },
  {
    title: 'Top Application',
    value: 'Microsoft Teams',
    subtitle: '26:45:00 across team',
  },
];

const employeeRows: EmployeeUsage[] = [
  {
    empId: 'SG-10451',
    name: 'Abhinav Mehta',
    department: 'Solution Team',

    totalHours: '09:24:00',
    workingHours: '08:48:00',
    productiveHours: '08:12:00',

    outlook: '01:08:20',
    teams: '01:53:20',
    edge: '00:22:10',
  },
  {
    empId: 'SG-10452',
    name: 'Sanjay Kumar',
    department: 'EAM',

    totalHours: '08:50:00',
    workingHours: '08:15:00',
    productiveHours: '07:45:00',

    outlook: '00:55:00',
    teams: '01:40:00',
    edge: '00:18:00',
  },
];

const EmployeeApplicationUsagePage = () => {
  const { hideLoader } = useLoaderContext();

  useEffect(() => {
  const timer = setTimeout(() => {
    hideLoader();
  }, 500);

  return () => clearTimeout(timer);
}, [hideLoader]);

  const [search, setSearch] =
    useState('');

  const [department, setDepartment] =
    useState('All Departments');

  const [
    productiveOnly,
    setProductiveOnly,
  ] = useState(false);

  const handleClearFilters = () => {
    setSearch('');
    setDepartment(
      'All Departments',
    );
    setProductiveOnly(false);
  };

  const filteredRows =
    useMemo(() => {
      return employeeRows.filter(
        (row) => {
          const matchesSearch =
            row.name
              .toLowerCase()
              .includes(
                search.toLowerCase(),
              ) ||
            row.empId
              .toLowerCase()
              .includes(
                search.toLowerCase(),
              );

          const matchesDepartment =
            department ===
              'All Departments' ||
            row.department ===
              department;

          if (
            !matchesSearch ||
            !matchesDepartment
          ) {
            return false;
          }

          if (
            productiveOnly
          ) {
            return (
              row.productiveHours !==
              '00:00:00'
            );
          }

          return true;
        },
      );
    }, [
      search,
      department,
      productiveOnly,
    ]);

  return (
    <div
      className="
        min-h-screen
        bg-[#F4F5F7]
        px-[6px]
        pb-[8px]
      "
    >
      <DashboardHeader
        breadcrumbs={[
          {
            label: 'Work Vista',
            path: '/',
          },
          {
            label:
              'Employee Application Usage Report',
          },
        ]}
      />

      <div
        className="
          -mt-[14px]
          rounded-[16px]
          border
          border-[#D0D5DD]
          bg-[#F8F9FB]
          p-[16px]
        "
      >
        <div
          className="
            mb-[14px]
            flex
            items-center
            gap-[10px]
          "
        >
          <h2
            className="
              text-[20px]
              font-semibold
              text-[#002B5B]
            "
          >
            Employee Application Usage Report
          </h2>

          <span
            className="
              rounded-full
              bg-[#EEF4FF]
              px-[10px]
              py-[4px]
              text-[11px]
              font-medium
              text-[#3538CD]
            "
          >
            ProHance
          </span>
        </div>

        <UsageSummaryCards
          cards={summaryCards}
        />

        <div
          className="
            mt-[14px]
            rounded-[12px]
            border
            border-[#D0D5DD]
            bg-white
            p-[12px]
          "
        >
          <UsageFilters
            search={search}
            department={department}
            productiveOnly={
              productiveOnly
            }
            onSearchChange={
              setSearch
            }
            onDepartmentChange={
              setDepartment
            }
            onProductiveToggle={
              setProductiveOnly
            }
            onClearFilters={
              handleClearFilters
            }
          />

          <div className="mt-3">
            <UsageLegend />
          </div>
        </div>

        <div className="mt-[14px]">
          <UsageTable
            rows={filteredRows}
          />
        </div>
      </div>
    </div>
  );
};

export default EmployeeApplicationUsagePage;